import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { ChevronRightIcon } from '../../../assets/icons';
import Pagination from '../../../components/Pagination';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { PLATFORM_TABLE_THEME } from './tableTheme';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetTemplatesQuery,
  useGetStateDepartmentsQuery,
  type TemplateListItem,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — Templates list (prototype `isTemplates`).
 * What each department is, written once. Templates are global. Clicking
 * a row opens its detail by code (/admin/templates/:code).
 * ------------------------------------------------------------------ */

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { code: stateCode, name: stateName } = usePlatformState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: templates = [], isLoading } = useGetTemplatesQuery();
  const { data: stateDepartments = [] } = useGetStateDepartmentsQuery(stateCode);
  const enabled = new Set(stateDepartments.map((d) => d.code));

  const total = templates.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rows = templates.slice((page - 1) * pageSize, page * pageSize);

  const columns: TableColumnsType<TemplateListItem> = [
    { title: '#', key: 'code', width: 64, render: (_, t) => <span className="text-xs text-ink-secondary">{t.code}</span> },
    { title: 'Template', key: 'name', render: (_, t) => <span className="text-sm font-semibold text-ink">{t.name}</span> },
    { title: 'Control', key: 'control', width: 150, render: (_, t) => <span className="text-xs text-[#434655]">{t.control}</span> },
    {
      title: 'Default levels',
      key: 'levels',
      width: 220,
      render: (_, t) => (
        <span className="whitespace-nowrap text-xs text-ink-secondary">{t.level_count} levels</span>
      ),
    },
    {
      title: 'Bottom unit',
      key: 'bottom',
      width: 150,
      render: (_, t) => <span className="text-xs text-[#434655]">{t.bottom_unit}</span>,
    },
    {
      title: 'States',
      key: 'states',
      width: 110,
      render: (_, t) => (
        <span className={['text-xs font-semibold', enabled.has(t.code) ? 'text-brand' : 'text-ink-secondary'].join(' ')}>
          {enabled.has(t.code) ? stateName : '—'}
        </span>
      ),
    },
    {
      title: '',
      key: 'chevron',
      width: 48,
      align: 'right',
      render: () => <ChevronRightIcon className="h-3 w-[7px] text-ink-secondary" />,
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: 'Template library' }]} />
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-brand">PLATFORM ADMIN · GLOBAL · ALL STATES</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Templates</h1>
        <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
          What each department is, written once: its levels, their default bindings, and the
          designation at each level with its rank. Each state’s profile starts from these, then adds
          or changes what differs there.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        {isLoading ? (
          <div className="p-6 text-sm text-ink-secondary">Loading templates…</div>
        ) : (
          <ConfigProvider theme={PLATFORM_TABLE_THEME}>
            <Table<TemplateListItem>
              className="chunaav-table"
              columns={columns}
              dataSource={rows}
              rowKey="code"
              pagination={false}
              scroll={{ x: 'max-content' }}
              onRow={(t) => ({ onClick: () => navigate(`/admin/templates/${t.code}`) })}
              rowClassName="cursor-pointer"
            />
          </ConfigProvider>
        )}

        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="templates"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
